import aiLogger from '../components/ai-logger';

export interface HistoryMessage {
    id: number;
    conversation_id?: number;
    query: string;
    response: string;
    liked: boolean;
    has_error: boolean;
    attachments: any[];
}

export interface HistoryResponse {
    history: HistoryMessage[];
    has_more: boolean;
}

export class ChatConversationService {
    private static readonly GUEST_SESSION_KEY = 'athena_guest_session_id';

    /**
     * Busca o histórico de mensagens de uma conversa.
     */
    public static async fetchHistory(conversationId: number | null, beforeId?: number): Promise<HistoryResponse> {
        const params = new URLSearchParams();
        if (beforeId) params.append('before_id', beforeId.toString());
        if (conversationId) params.append('conversation_id', conversationId.toString());
        
        const url = `/chat/history${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return await response.json();
    }

    /**
     * Marca uma mensagem como interrompida.
     */
    public static async markInterrupted(messageId: number, csrfToken: string): Promise<void> {
        if (!messageId) return;
        try {
            await fetch(`/chat/message/${messageId}/interrupted`, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrfToken,
                },
            });
        } catch (e) {
            aiLogger.warn('[ChatConversationService] Failed to mark interrupted:', e);
        }
    }

    /**
     * Migra mensagens da sessão guest para a conta logada.
     */
    public static async migrateGuestSession(csrfToken: string): Promise<number> {
        const sessionId = this.getGuestSessionId();
        if (!sessionId) return 0;

        const migrateData = new FormData();
        migrateData.append('session_id', sessionId);

        const response = await fetch('/chat/migrate-session', {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': csrfToken,
                'Accept': 'application/json',
                'X-Chat-Session-Id': sessionId,
            },
            body: migrateData
        });

        if (response.ok) {
            const result = await response.json();
            this.clearGuestSessionId();
            return result.migrated || 0;
        }
        return 0;
    }

    /**
     * Retorna o ID da sessão guest do storage.
     */
    public static getGuestSessionId(): string | null {
        for (const storage of [localStorage, sessionStorage]) {
            try {
                const val = storage.getItem(this.GUEST_SESSION_KEY);
                if (val) return val;
            } catch { /* ignored */ }
        }
        return null;
    }

    /**
     * Garante a existência de um ID de sessão guest.
     */
    public static getOrCreateGuestSessionId(): string {
        let id = this.getGuestSessionId();
        if (id) return id;

        id = crypto.randomUUID 
            ? crypto.randomUUID() 
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            
        for (const storage of [localStorage, sessionStorage]) {
            try {
                storage.setItem(this.GUEST_SESSION_KEY, id);
                return id;
            } catch { /* ignored */ }
        }
        return id;
    }

    public static clearGuestSessionId(): void {
        for (const storage of [localStorage, sessionStorage]) {
            try { storage.removeItem(this.GUEST_SESSION_KEY); } catch { /* ignored */ }
        }
    }
}
